import { ENABLED_AUTOMATION_STATUSES } from '@reach/shared-types/business-automation';
import type { StatusIndicator } from '../../../components/tenant-selector/types';

/** Label substring used to detect tenants with the automation actively on (cannot deselect). */
export const SYNCED_AND_ON_LABEL = 'In Use';

/** Same UI copy for synced-but-off (draft) and other idle states; use `automationStatusesToIndicator` for badge color. */
export const SYNCED_AND_OFF_LABEL = 'Available';

export const HIDDEN_LABEL = 'Hidden';

/**
 * Statuses that read as enabled/In Use. Shares {@link ENABLED_AUTOMATION_STATUSES} with the backend
 * status aggregation, plus the legacy `'scheduled'` string (never emitted by the current backend, but
 * kept so any historical value still reads as enabled).
 */
const ENABLED_STATUSES = new Set<string>([...ENABLED_AUTOMATION_STATUSES, 'scheduled']);
const HIDDEN_STATUSES = new Set<string>(['archived', 'failed']);

/**
 * Map backend automation status to the label shown in the Tenants tab. Anything not hidden or enabled
 * (draft, completed, cancelled, partially_cancelled, unknown) reads as {@link SYNCED_AND_OFF_LABEL}.
 */
export function automationStatusToTenantLabel(status: string): string {
  if (HIDDEN_STATUSES.has(status)) return HIDDEN_LABEL;
  if (ENABLED_STATUSES.has(status)) return SYNCED_AND_ON_LABEL;
  return SYNCED_AND_OFF_LABEL;
}

/**
 * Badge color for a tenant row from raw automation statuses. Required because draft and completed
 * both display as {@link SYNCED_AND_OFF_LABEL}.
 */
export function automationStatusesToIndicator(statuses: readonly string[]): StatusIndicator {
  if (statuses.length === 0) return 'neutral';

  let hasHidden = false;
  let hasOn = false;
  let hasDraft = false;

  for (const status of statuses) {
    if (HIDDEN_STATUSES.has(status)) hasHidden = true;
    else if (status === 'draft') hasDraft = true;
    else if (ENABLED_STATUSES.has(status)) hasOn = true;
  }

  if (hasHidden) return 'hidden';
  if (hasOn) return 'warning';
  if (hasDraft) return 'good';
  return 'neutral';
}
